import {PlaceAutocompletePrediction, PlaceResult} from '@store/types/google'

let sessionToken: google.maps.places.AutocompleteSessionToken | null = null
const LEGACY_TO_NEW_FIELD_MAP: Record<string, string> = {
  'address_components': 'addressComponents',
  'formatted_address': 'formattedAddress',
  'geometry': 'location',
  'name': 'displayName',
} as const;

function getSessionToken(): google.maps.places.AutocompleteSessionToken {
  if (!sessionToken && window?.google?.maps) {
    sessionToken = new window.google.maps.places.AutocompleteSessionToken()
  }
  return sessionToken
}

function checkAPIReadiness() {
  if(!window?.google?.maps) {
    throw new Error('Google API is not present!!!');
  }
}

function resetSessionToken(): void {
  sessionToken = null
}

export async function fetchSuggestions(options: {
  input: string
  includedRegionCodes?: string[]
  includedPrimaryTypes?: string[]
}): Promise<PlaceAutocompletePrediction[]> {
  const request: google.maps.places.AutocompleteRequest = {
    input: options.input,
    sessionToken: getSessionToken(),
  }

  if (options.includedRegionCodes?.length) {
    request.includedRegionCodes = options.includedRegionCodes
  }
  if (options.includedPrimaryTypes?.length) {
    request.includedPrimaryTypes = options.includedPrimaryTypes
  }

  checkAPIReadiness()
  const {suggestions} = await window.google.maps.places.AutocompleteSuggestion
    .fetchAutocompleteSuggestions(request)

  return suggestions
  .filter(s => s.placePrediction)
  .map(s => {
    const pred = s.placePrediction!
    return {
      description: pred.text?.text || '',
      place_id: pred.placeId || '',
      reference: pred.placeId || '',
      types: pred.types || [],
      matched_substrings: [],
      terms: [],
      structured_formatting: {
        main_text: pred.structuredFormat?.mainText?.text || '',
        main_text_matched_substrings: [],
        secondary_text: pred.structuredFormat?.secondaryText?.text || '',
        secondary_text_matched_substrings: [],
      },
    }
  })
}

export async function fetchPlaceDetails(options: {
  placeId: string
  fields?: string[]
}): Promise<PlaceResult | null> {
  checkAPIReadiness()
  const place = new window.google.maps.places.Place({id: options.placeId})

  const requestFields = (options.fields || ['addressComponents', 'formattedAddress', 'location', 'displayName'])
    .map(f => LEGACY_TO_NEW_FIELD_MAP[f] || f)

  await place.fetchFields({
    fields: requestFields,
    sessionToken: getSessionToken(),
  })

  resetSessionToken()

  return normalizePlaceData(place)
}

export function normalizePlaceData(place: google.maps.places.Place): PlaceResult {
  const lat = place.location?.lat() ?? 0
  const lng = place.location?.lng() ?? 0

  return {
    place_id: place.id || '',
    address_components: (place.addressComponents || []).map(c => ({
      long_name: c.longText || '',
      short_name: c.shortText || '',
      types: c.types || [],
    })),
    formatted_address: place.formattedAddress || '',
    name: place.displayName || '',
    geometry: {
      location: {
        lat: lat,
        lng: lng,
      },
    },
  }
}
